// hooks/usePermissions.js
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';

export const usePermissions = () => {
  const { user, isParent } = useAuth();
  const [permissions, setPermissions] = useState({
    peutPartagerFeed: false,
    peutAccederMessages: false,
    enfantsPermissions: {}, // Permissions par enfant
    role: null,
    loading: true // ✅ Ajout dans le state initial
  });

  useEffect(() => {
    if (user) {
      chargerPermissions();
    } else {
      // ✅ Reset des permissions si pas d'utilisateur
      setPermissions(prev => ({
        ...prev,
        loading: false
      }));
    }
  }, [user]);

  const chargerPermissions = async () => {
    try {
      // ✅ Si c'est un parent, permissions complètes
      if (isParent || user.role === 'parent') {
        setPermissions({
          peutPartagerFeed: true,
          peutAccederMessages: true,
          enfantsPermissions: {},
          role: 'parent',
          loading: false
        });
        return;
      }

      // ✅ Si c'est un membre, permissions standard
      if (user.role === 'membre') {
        setPermissions({
          peutPartagerFeed: true,
          peutAccederMessages: true,
          enfantsPermissions: {},
          role: 'membre',
          loading: false
        });
        return;
      }

      // ✅ Pour les autres rôles, récupérer les permissions depuis le backend
      const response = await API.utilisateurs.getMesPermissions();

      if (response.success) {
        const perms = response.data;

        // Calculer les permissions globales (si au moins un enfant autorise)
        const peutPartagerFeed = Object.values(perms.enfantsPermissions || {}).some(p =>
          p.permissions.includes('partager_feed') || p.permissions.includes('all')
        );

        const peutAccederMessages = Object.values(perms.enfantsPermissions || {}).some(p =>
          p.permissions.includes('acceder_messages') || p.permissions.includes('all')
        );

        setPermissions({
          peutPartagerFeed,
          peutAccederMessages,
          enfantsPermissions: perms.enfantsPermissions || {},
          role: perms.role || 'membre',
          loading: false
        });
      } else {
        throw new Error('Réponse API invalide');
      }

    } catch (error) {
      // Error loading permissions

      // ✅ En cas d'erreur, permissions minimales avec structure cohérente
      setPermissions({
        peutPartagerFeed: false,
        peutAccederMessages: false,
        enfantsPermissions: {},
        role: 'membre',
        loading: false
      });
    }
  };

  // Vérifier si l'utilisateur a une permission spécifique pour un enfant
  const aPermissionPourEnfant = (enfantId, permission) => {
    // ✅ Parents ont toutes les permissions
    if (isParent || user?.role === 'parent') {
      return true;
    }

    const enfantPerms = permissions.enfantsPermissions[enfantId];
    if (!enfantPerms) return false;

    return enfantPerms.permissions.includes(permission) ||
           enfantPerms.permissions.includes('all');
  };

  return {
    peutPartagerFeed: permissions.peutPartagerFeed,
    peutAccederMessages: permissions.peutAccederMessages,
    enfantsPermissions: permissions.enfantsPermissions,
    role: permissions.role,
    loading: permissions.loading,
    aPermissionPourEnfant,
    rechargerPermissions: chargerPermissions
  };
};